import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import FileUpload from "@/components/FileUpload";
import TesterDeviceInput from "@/components/TesterDeviceInput";
import { useAuth } from "@/context/AuthContext";
import { Plus, Download, Search, X } from "lucide-react";
import { toast } from "sonner";

const SEV = ["critical", "high", "medium", "low"];
const ISSUE_STATUS = ["New", "Open", "Fixed", "Ready for test", "Rejected", "Cancelled", "Done"];

export default function Issues() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [issues, setIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", title: "", steps_to_reproduce: "", expected_result: "", actual_result: "", severity: "medium", environment: "", device_browser: "", attachments: [] });
  const [filters, setFilters] = useState({ q: "", project_id: "", status: "", severity: "", reporter_id: "", task_id: "", date_from: "", date_to: "" });
  const [testers, setTesters] = useState([]);

  const canExport = user?.role === "admin" || user?.role === "manager";

  const load = async () => {
    const params = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v && v !== "all") params[k] = v;
    }
    const { data } = await api.get("/issues", { params });
    setIssues(data || []);
    if (projects.length === 0) {
      const { data: p } = await api.get("/projects");
      setProjects(p || []);
    }
    if (canExport && testers.length === 0) {
      try {
        const { data: t } = await api.get("/users", { params: { role: "freelancer", limit: 500 } });
        setTesters(Array.isArray(t) ? t : (t?.items || []));
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); }, [filters]);

  const activeFilterCount = useMemo(() => Object.entries(filters).filter(([k, v]) => v && v !== "all" && k !== "q").length, [filters]);
  const clearFilters = () => setFilters({ q: "", project_id: "", status: "", severity: "", reporter_id: "", task_id: "", date_from: "", date_to: "" });

  const exportCsv = () => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v && v !== "all") params.set(k, v);
    }
    const qs = params.toString();
    const url = `${API}/issues/export.csv${qs ? `?${qs}` : ""}`;
    // Open in same tab — server sets Content-Disposition so the browser downloads.
    window.location.href = url;
  };

  const create = async () => {
    if (!form.project_id || !form.title) { toast.error("Title and project are required"); return; }
    try {
      await api.post("/issues", form);
      toast.success("Issue reported");
      setOpen(false);
      setForm({ project_id: "", title: "", steps_to_reproduce: "", expected_result: "", actual_result: "", severity: "medium", environment: "", device_browser: "", attachments: [] });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <AppLayout title="Issues" subtitle="Bug reports">
      {/* Filters + actions */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
              <Input
                value={filters.q}
                onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                placeholder="Title or steps…"
                className="pl-9"
                autoCapitalize="none"
                autoCorrect="off"
                data-testid="input-issue-search"
              />
            </div>
          </div>
          <div className="w-[180px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Project</Label>
            <Select value={filters.project_id || "all"} onValueChange={v => setFilters(f => ({ ...f, project_id: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-project"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[160px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Status</Label>
            <Select value={filters.status || "all"} onValueChange={v => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {ISSUE_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[140px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Severity</Label>
            <Select value={filters.severity || "all"} onValueChange={v => setFilters(f => ({ ...f, severity: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-severity"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {SEV.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[220px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Reporter</Label>
            <Select value={filters.reporter_id || "all"} onValueChange={v => setFilters(f => ({ ...f, reporter_id: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-reporter"><SelectValue placeholder="All"/></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All reporters</SelectItem>
                {testers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} — {t.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[180px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Task ID</Label>
            <Input
              value={filters.task_id}
              onChange={e => setFilters(f => ({ ...f, task_id: e.target.value.trim() }))}
              placeholder="Paste task ID"
              className="font-mono text-xs"
              autoCapitalize="none"
              autoCorrect="off"
              data-testid="filter-task-id"
            />
          </div>
          <div className="w-[150px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">From</Label>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from"/>
          </div>
          <div className="w-[150px]">
            <Label className="text-[11px] uppercase text-slate-500 tracking-wider">To</Label>
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to"/>
          </div>
          {activeFilterCount > 0 && (
            <Button variant="ghost" onClick={clearFilters} className="text-slate-500" data-testid="btn-clear-filters">
              <X className="w-3.5 h-3.5 mr-1"/>Clear ({activeFilterCount})
            </Button>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-500" data-testid="issues-count">
            {issues.length} issue{issues.length === 1 ? "" : "s"}{activeFilterCount > 0 ? " · filtered" : ""}
          </div>
          <div className="flex items-center gap-2">
            {canExport && (
              <Button variant="outline" onClick={exportCsv} disabled={issues.length === 0} data-testid="btn-export-csv">
                <Download className="w-4 h-4 mr-1.5"/>Export CSV
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-issue"><Plus className="w-4 h-4 mr-1.5"/>Report issue</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-auto">
                <DialogHeader><DialogTitle>Report a new issue</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Project</Label>
                    <Select value={form.project_id} onValueChange={v=>setForm(f=>({...f, project_id:v}))}>
                      <SelectTrigger data-testid="select-issue-project"><SelectValue placeholder="Choose project"/></SelectTrigger>
                      <SelectContent>{projects.map(p=> <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Title</Label><Input value={form.title} onChange={e=>setForm(f=>({...f, title:e.target.value}))} data-testid="input-issue-title"/></div>
                  <div><Label>Steps to reproduce</Label><Textarea rows={3} value={form.steps_to_reproduce} onChange={e=>setForm(f=>({...f, steps_to_reproduce:e.target.value}))} data-testid="input-issue-steps"/></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Expected result</Label><Textarea rows={2} value={form.expected_result} onChange={e=>setForm(f=>({...f, expected_result:e.target.value}))} data-testid="input-issue-expected"/></div>
                    <div><Label>Actual result</Label><Textarea rows={2} value={form.actual_result} onChange={e=>setForm(f=>({...f, actual_result:e.target.value}))} data-testid="input-issue-actual"/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Severity</Label>
                      <Select value={form.severity} onValueChange={v=>setForm(f=>({...f, severity:v}))}>
                        <SelectTrigger data-testid="select-severity"><SelectValue/></SelectTrigger>
                        <SelectContent>{SEV.map(s=> <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Environment</Label><Input value={form.environment} onChange={e=>setForm(f=>({...f, environment:e.target.value}))} placeholder="iOS 17 / Chrome 121" data-testid="input-env"/></div>
                  </div>
                  <div>
                    <Label>Device / browser</Label><TesterDeviceInput value={form.device_browser} onChange={v => setForm(f => ({ ...f, device_browser: v }))} placeholder="iPhone 15 Pro" testid="input-device"/>
                  </div>
                  <div><Label>Screenshot / log</Label>
                    <FileUpload testid="upload-issue-attachment" onUploaded={(d)=>setForm(f=>({...f, attachments:[...f.attachments, d]}))}/>
                    {form.attachments.length > 0 && <div className="mt-1 text-xs text-slate-600">{form.attachments.length} attached</div>}
                  </div>
                </div>
                <DialogFooter><Button onClick={create} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-submit-issue">Submit issue</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table">
          <thead><tr>
            <th>Title</th><th>Project</th><th>Severity</th><th>Status</th><th>Reporter</th><th>Created</th>
          </tr></thead>
          <tbody>
            {issues.map(i => (
              <tr key={i.id} className="cursor-pointer" onClick={()=>nav(`/issues/${i.id}`)} data-testid={`row-issue-${i.id}`}>
                <td>
                  <span className="font-mono text-[11px] text-slate-400 mr-2">BUG-{i.id.slice(0,4).toUpperCase()}</span>{i.title}
                  {i.suspected_duplicate_of && (
                    <span className="ml-2 inline-flex items-center text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" title={i.dedup_reason || `${Math.round((i.dedup_confidence||0)*100)}% similar`} data-testid={`dup-badge-${i.id}`}>
                      Possible duplicate
                    </span>
                  )}
                </td>
                <td>{i.project_name}</td>
                <td><StatusBadge kind="severity" value={i.severity}/></td>
                <td><StatusBadge kind="issue" value={i.status}/></td>
                <td>{i.reporter_name}</td>
                <td className="text-xs text-slate-500">{new Date(i.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {issues.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No issues yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="issues-mobile-list">
        {issues.length === 0 && <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-slate-500">No issues yet.</div>}
        {issues.map(i => (
          <button key={i.id} onClick={()=>nav(`/issues/${i.id}`)} className="w-full text-left bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors" data-testid={`card-issue-${i.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[10px] text-slate-400 mb-0.5">BUG-{i.id.slice(0,4).toUpperCase()}</div>
                <div className="font-display font-semibold text-slate-900 text-sm line-clamp-2">{i.title}</div>
                {i.suspected_duplicate_of && (
                  <div className="mt-1 inline-flex items-center text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" data-testid={`dup-badge-mobile-${i.id}`}>
                    Possible duplicate
                  </div>
                )}
              </div>
              <StatusBadge kind="issue" value={i.status}/>
            </div>
            <div className="text-xs text-slate-500 mb-2">{i.project_name}</div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <StatusBadge kind="severity" value={i.severity}/>
              <span className="text-slate-500">•</span>
              <span className="text-slate-600">{i.reporter_name}</span>
              <span className="text-slate-500">•</span>
              <span className="text-slate-500">{new Date(i.created_at).toLocaleDateString()}</span>
            </div>
          </button>
        ))}
      </div>
    </AppLayout>
  );
}
